import {
    Button,
    Collapsible,
    CollapsibleContent,
    CollapsibleHeader,
    Inline,
} from '@george-labs.com/design-system';
import type { FC, ReactNode } from 'react';

import { sanitize } from '@luna/utils';
import { t } from '@luna/i18n';
import type { PublicTemporaryMessage } from '@api-client/temporary-messages';

import style from './TemporaryMessagesHeader.module.scss';
import { useTemporaryMessagesState } from '../useTemporaryMessagesState';

interface Props {
    message: PublicTemporaryMessage;
    afterMessage: ReactNode;
    onClose(messageId: string): void;
}

export const TemporaryMessageContent: FC<Props> = ({ message, afterMessage, onClose }) => {
    const { collapsedMessagesIds, dispatchActions } = useTemporaryMessagesState();
    const isCollapsed = collapsedMessagesIds.includes(message.id);

    const title = (
        <Inline className="text-truncate py-1" emphasis data-cy="temporary-message-title">
            {message.localizedText.title}
        </Inline>
    );

    const content = (
        <>
            <div
                className={`${style.temporaryMessageTextClamp} g-small`}
                data-cy="temporary-message-body"
                dangerouslySetInnerHTML={{
                    __html: sanitize(
                        message.localizedText.preview ?? message.localizedText.body ?? '',
                        {
                            FORBID_TAGS: ['br', 'p'],
                        },
                    ),
                }}
            />
            {afterMessage}
        </>
    );

    if (message.closeable) {
        return (
            <>
                <div className="d-flex justify-content-between">
                    {title}
                    <Button
                        data-cy="temporary-message-close-option"
                        variant={Button.VARIANT.TERTIARY}
                        size={Button.SIZE.SMALL}
                        icon={Button.ICON.ERROR}
                        fullWidth={Button.FULL_WIDTH.NEVER}
                        hiddenLabel={Button.HIDDEN_LABEL.BELOW_MD}
                        iconClassName="g-button-icon-right"
                        onClick={() => onClose(message.id)}
                    >
                        {t('temporaryMessages.dismissButton')}
                    </Button>
                </div>
                {content}
            </>
        );
    }

    const { collapseMessage, expandMessage } = dispatchActions;

    return (
        <Collapsible
            isOpen={!isCollapsed}
            onClose={() => collapseMessage(message.id)}
            onOpen={() => expandMessage(message.id)}
        >
            <CollapsibleHeader data-cy="temporary-message-header">{title}</CollapsibleHeader>
            <CollapsibleContent>{content}</CollapsibleContent>
        </Collapsible>
    );
};
